import type { SxProps } from '@mui/material';
import type { TCompany } from 'src/organization/types';

import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullscreenLoadingIndicator } from 'src/shared/components/FullscreenLoadingIndicator/FullscreenLoadingIndicator';

export type CompanyListSelectorProps = {
  isLoading: boolean;
  items: TCompany[];
  onSelectItem: (item: TCompany) => void;
  sx?: SxProps;
} & React.HTMLAttributes<HTMLDivElement>;

export const CompanyListSelector: React.FC<CompanyListSelectorProps> = ({
  isLoading,
  items,
  onSelectItem,
  sx,
  ...divProps
}) => {
  const [isReady, setIsReady] = useState(false);
  const { t } = useTranslation('organization');

  useEffect(() => {
    setIsReady(items.length > 1);
    if (items.length === 1) {
      onSelectItem(items[0]);
    }
  }, [isLoading, items, onSelectItem]);

  if (!isReady) {
    return <FullscreenLoadingIndicator />;
  }

  if (isLoading) {
    return (
      <Stack spacing={2} {...divProps} sx={sx}>
        {[...Array(10)].map((_, index) => (
          <Card key={index} sx={{ p: 2 }}>
            <Skeleton height={32} />
            <Skeleton height={24} />
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} {...divProps} sx={sx}>
      <Box>
        <Typography variant="h3">{t('SELECT_COMPANY')}</Typography>
        <Typography variant="body1">
          {t('SELECT_COMPANY_DESCRIPTION')}
        </Typography>
      </Box>
      <Stack spacing={2}>
        {items.map((item) => (
          <Card
            key={item.id}
            onClick={() => onSelectItem(item)}
            sx={{
              '&:hover': {
                backgroundColor: 'background.neutral',
              },
              cursor: 'pointer',
              p: 2,
            }}
          >
            <Typography variant="h6">{item.name}</Typography>
            <Typography color="text.secondary" variant="body2">
              {item.organization_number}
            </Typography>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};
